import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  createMeterReader,
  deleteManufacturere,
  getActiveMeterReadingStatuses,
  getMeterReaderList,
  getMeterReadingFormat,
  getMeterReadingFormatDetail,
  getMeterReadingStatusList,
  getMeterReasonCodesList,
  getUserUtilityServices,
  patchMeterReadingFormat,
  postMeterReadingFormat,
  postMeterReadingStatus,
  postMeterReasonCodes,
  updateMeterReader,
  updateMeterReadingStatus,
  updateMeterReasonCodes,
} from "./api";
import { queryClient } from "@shared/api/queries/queryClient";
import { useToast } from "@shared/hooks/use-toast";
import { toast } from "@shared/ui/use-toast"; 
export const useMeterReadingFormat = (
  remoteUtilityId: string,
  search: string
) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.readingFormatList(search),
    () => getMeterReadingFormat(remoteUtilityId, search),
    {
      staleTime: 0,
      enabled: !!remoteUtilityId,
      refetchOnMount: true,
    }
  );

export const useUserUtilityServices = (utilityId: string) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.userUtilityServices(),
    () => getUserUtilityServices(utilityId),
    {
      staleTime: 30 * 60 * 1000,
      enabled: !!utilityId,
    }
  );

export const useDeleteManufacture = () => {
  const { toast } = useToast();
  return useSmartMutation((id: number) => deleteManufacturere(id), {
    onSuccess: (_data, id) => {
      toast({
        title: "Success",
        description: "Manufacturer deleted successfully!",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["manufacturer", "meterManufacturers"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const usePostMeterReadingFormat = () =>
  useSmartMutation(postMeterReadingFormat, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Format created successfully.",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "format-list"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

export const useMeterReadingFormatDetail = (id: string, remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewMeterReadingFormatDetail(id),
    () => getMeterReadingFormatDetail(id, remoteUtilityId),
    { staleTime: 0, refetchOnMount: true, enabled: !!remoteUtilityId && !!id }
  );
};

export const usePatchMeterReadingFormat = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      patchMeterReadingFormat(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Format updated successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "format-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reading-format-detail"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );

export const useMeterReaderList = ({
  page,
  remoteUtilityId,
  search,
}: {
  page: number;
  remoteUtilityId: string;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewMeterReaders(page, search),
    () => getMeterReaderList({ page, remoteUtilityId, search }),
    { enabled: !!remoteUtilityId }
  );
};

export const useUpdateMeterReader = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReader(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Meter Reader updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reader"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );

export const useMeterReadingStatusList = (
  remoteUtilityId: string,
  page: number,
  limit: any,
  search?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReadingStatusList(page, search),
    () => getMeterReadingStatusList(remoteUtilityId, page, limit, search),
    { enabled: !!remoteUtilityId }
  );
};

export const useUpdateMeterReadingStatus = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReadingStatus(String(id), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reading-status"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );

export const usePostMeterReadingStatus = () =>
  useSmartMutation(postMeterReadingStatus, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meter reading status added successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "meter-reading-status"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

export const useMeterReasonCodesList = (
  remoteUtilityId: string,
  page: number,
  limit: number,
  search?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReasonCodesList(page, search),
    () => getMeterReasonCodesList(remoteUtilityId, page, limit, search),
    { enabled: !!remoteUtilityId }
  );
};

export const useUpdateMeterReasonCodes = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReasonCodes(String(id), payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Activation status updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "reason-code-list"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );

export const usePostMeterReasonCodes = () =>
  useSmartMutation(postMeterReasonCodes, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mx", "reason-code-list"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

export const useActiveMeterReadingStatuses = (remoteUtilityId: string) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewActiveMeterStatuses(),
    () => getActiveMeterReadingStatuses(remoteUtilityId),
    { enabled: !!remoteUtilityId }
  );

export const useCreateMeterReader = () =>
  useSmartMutation(createMeterReader, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meter reader created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "meter-reader"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
